export const ServiceReviewDocumentationMarkdown = `
# Payor Communication Engine Block

## Overview

The **Payor Communication Engine** block provides a unified interface for submitting prior authorization requests and notices of admission to insurance payer portals electronically. Instead of building separate integrations for each payer's unique submission method, this block handles the complexity of multiple protocols, formats, and response patterns.

### What It Does

The block automates the submission of healthcare transactions to insurance payers, including:

- **Prior Authorizations**: Requests for approval before performing medical procedures
- **Notices of Admission (NOA)**: Notifications of patient hospital admissions
- **Status Inquiries** (Coming Soon): Checking the status of previously submitted requests

### Key Benefits

- **Unified Interface**: Single block configuration works across multiple payers and submission methods
- **Automatic Protocol Handling**: Supports both API and EDI (X12 278) submissions without changing your flow
- **Normalized Responses**: Returns consistent data structures regardless of payer-specific formats
- **Async Support**: Handles both immediate and delayed responses from payers
- **Flexible Configuration**: Override provider information, customize payloads, and route based on your organization's needs

### When to Use This Block

Use the Payor Communication Engine when you need to:

- Obtain authorization approvals from insurance payers before performing medical procedures
- Submit notice of admission notifications for inpatient stays
- Automate manual prior authorization workflows
- Standardize authorization submissions across multiple payer portals

---

## Supported Payer Integrations

The block supports multiple payer networks through different submission methods:

### Capability Matrix

| Payer/Network | Method | Outpatient Auth | NOA | Inquiry |
|---------------|--------|-----------------|-----|---------|
| **EviCore** (multiple payers) | EDI | ✓ | - | - |
| **Availity** (multiple payers) | API | ✓ | ✓ | ✓ |
| **PNT Data** (multiple payers) | EDI | ✓ | ✓ | ✓ |

**Legend:**
- ✓ = Supported
- - = Not supported

**Note**: Specific payer support vary by state, plan, and specialty. Contact the Notable team to confirm capabilities for your specific payers.

### Submission Methods Explained

**API Submissions**
- Modern RESTful API integrations
- Typically provide immediate responses
- JSON-based request and response formats
- Available for: Availity

**EDI (X12 278) Submissions**
- Electronic Data Interchange using healthcare standards
- May have delayed responses (asynchronous)
- X12 formatted transactions
- Available for: EviCore, PNT Data

---

## Understanding EDI and Healthcare Transactions

### What is X12 278?

X12 278 is a standardized healthcare transaction format used for service review requests and responses. It's part of the EDI (Electronic Data Interchange) standard and consists of:

- **Segments**: Individual lines of data, each starting with a segment identifier (e.g., "ISA", "ST", "NM1")
- **Elements**: Data fields within segments, separated by delimiters
- **Transaction Sets**: Complete messages wrapped in headers and trailers

A typical 278 message includes:
- Patient demographics (name, date of birth, member ID)
- Provider information (NPI, name, address)
- Procedure details (CPT codes, diagnosis codes)
- Service dates and quantities
- Payer identification

### Transaction Types

**278R - Authorization Request**
- Used to request prior authorization for medical procedures
- Can be for outpatient procedures, inpatient scheduled procedures, or emergency admissions
- Payer responds with approval, denial, or request for more information

**278N - Notice of Admission**
- Notification to payer that a patient has been admitted
- Required by many payers for inpatient stays
- Not a request for authorization, but an informational notice

**278I - Status Inquiry**
- Query to check the status of a previously submitted authorization request
- Returns current status and any additional information from the payer

---

## Configuration Guide

### Required Settings

All Payor Communication Engine blocks require these configurations:

#### "patientOrder" (string, required)

An object containing the complete patient order information. This typically comes from an upstream block that retrieves the patient order.

The patient order must include:
- Patient demographics
- Insurance information
- Procedure details with CPT codes (for authorizations)
- Diagnosis codes
- Provider information
- Service dates

**Example**: "{{GetPatientOrder.data.patientOrderJson}}"

#### "submissionMode" (string, required)

The submission method to use for this request.

Options:
- ""API"" - For Availity submissions
- ""278"" - For EviCore and PNT Data EDI submissions

Choose based on which payer portal you're submitting to.

#### "payorPortal" (string, required)

The payer portal to submit the request to.

Options:
- ""Availity"" - For Availity API submissions
- ""EviCore"" - For EviCore EDI submissions
- ""PNT Data"" - For PNT Data EDI submissions

Must be compatible with the selected submission mode.

#### "patientInsuranceId" (string, required)

The ID of the specific insurance plan to submit to. This is particularly important for patients with multiple insurance coverages.

**Example**: "{{GetPatientOrder.data.primaryInsuranceId}}"

#### "timeout" (duration, required)

Maximum time to wait for asynchronous responses from the payer. Used primarily for EDI submissions where responses may not be immediate.

### Optional Settings

#### "customPayload" (string, optional)

A custom request payload to send to the payer portal. Format depends on the submission mode:

**For EDI (278) submissions**: A complete X12 278 transaction string
**For API submissions**: A JSON string conforming to the payer's API schema

If not provided:
- API submissions will build the payload automatically from the patient order
- EDI submissions **require** this field (see payer-specific sections below)

See examples in the Use Cases section.

#### "isTest" (boolean, optional)

Whether to use test/sandbox mode for the payer integration.

**Default**: "true"

When "true":
- Uses test endpoints and credentials
- Does not submit to production payer systems
- Useful for integration testing and validation

When "false":
- Uses production endpoints
- Submits real authorization requests
- Affects payer records and patient coverage

**Important**: Set to "false" for production flows after testing is complete.


### Payer-Specific Configuration

#### Availity (API)

**Configuration**:
- "submissionMode": ""API""
- "payorPortal": ""Availity""
- "customPayload": Optional

**Characteristics**:
- Provides immediate (synchronous) responses in most cases
- Custom payload is optional; will build from patient order if not provided
- Test mode fully supported


#### EviCore (EDI 278)

**Configuration**:
- "submissionMode": ""278""
- "payorPortal": ""EviCore""
- "customPayload": **Required**

**Characteristics**:
- Asynchronous responses - may take hours
- Supports per-procedure responses
- Used by multiple payers (Aetna, Cigna, Anthem)
- Test mode supported

**Important**: Responses may take time to arrive. Configure your timeout settings to allow sufficient time for payer processing. The block will wait for responses and handle them as they arrive.

#### PNT Data (EDI 278)

**Configuration**:
- "submissionMode": ""278""
- "payorPortal": ""PNT Data""
- "customPayload": **Required**

**Characteristics**:
- Supports multiple payers through a single integration
- Asynchronous response processing

<Warning>
**Test Mode Not Supported**: PNT Data integrations do not support test mode. The "isTest" setting must be set to "false". Attempting to use test mode will result in an error.
</Warning>

---

## Block Outputs

The Payor Communication Engine block returns different output structures depending on the type of submission. Always check the "outputVersion" field to determine how to process the response.

### Output Types

There are two possible output formats:

1. **Authorization Submissions** - Used for prior authorization requests (EviCore 278)
2. **Service Review Submissions** - Used for NOA submissions (Availity API; note that PNT Data persists submissions on the patient order but does not currently populate the block output)

### Authorization Submissions Output

**Used when:** Submitting to EviCore for prior authorizations

**Output Structure:**

The block returns an object with the following fields:

- "outputVersion": Always set to ""authorization_submissions_v2""
- "allResponsesReceived": Boolean indicating if all procedures received responses
- "authorizationSubmissions": Array of authorization submission objects (optional)
- "authorizationStatus": Overall authorization status object (optional)
- "errorResponse": Error details if submission failed (optional)

#### Field: "outputVersion"

Always ""authorization_submissions_v2"" for this output type. Use this to identify which output structure you're working with.

#### Field: "authorizationSubmissions"

Array of enriched authorization submission objects. Each authorization submission in the array contains:

**Procedure Information:**
- "procedures": List of procedures this authorization covers (includes CPT codes, descriptions, quantities)
- Procedure IDs are available through the procedures list

**Status Information:**
- "status": Authorization status - one of: "APPROVED", "REJECTED", "EXCEPTION", "NOT_REQUIRED", "PENDING", "UNKNOWN"
- "rawStatus": Raw status string from the payer
- "authorizationNumber": Payer's authorization number
- "trackingNumber": Payer's tracking or reference number

**Date Information:**
- "requestedDate": When the authorization was requested
- "obtainedDate": When the authorization was obtained from the payer
- "effectiveDate": Date the authorization becomes effective
- "expirationDate": Date the authorization expires

**Status-Specific Details:**

When status is "NOT_REQUIRED":
- "notRequiredReason": Why authorization wasn't required
- "notRequiredComment": Additional details

When status is "EXCEPTION":
- "exceptionReason": What went wrong
- "exceptionComment": Additional details (for timeouts, this will be ""Portal did not respond within timeout"")

When status is "REJECTED":
- "rejectedReason": Why the authorization was rejected
- "rejectedComment": Additional details from the payer

When status is "PENDING":
- "pendingReason": Why the request is pending
- "pendingComment": Additional information needed

**Additional Fields:**
- "payorPortal": Which portal handled the authorization (e.g., "EVICORE")
- "submissionMethod": How it was submitted - for this block, will be "Api"
- "verificationGsPath": Storage path for verification file
- "fileReference": Resolved reference for the verification file (for accessing the document)
- "rawOutput": Original response payload from the payer (useful for debugging)

#### Field: "authorizationStatus"

Overall authorization status for the entire patient order, aggregating all procedure-level submissions. Contains:

**Status Information:**
- "status": Overall authorization status across all procedures
- "authorizationRequired": Whether authorization is required for this order
- "reason": Reason for the overall status

**Identifiers:**
- "authorizationNumber": High-level authorization number
- "trackingNumber": High-level tracking identifier

**Date Information (as strings):**
- "requestedDate": When authorization was requested
- "obtainedDate": When authorization was obtained
- "effectiveDate": When authorization becomes effective
- "expirationDate": When authorization expires

**Additional Information:**
- "note": Any notes about the authorization
- "rejectedComment": Details if the authorization was rejected
- "patientType": Type of patient (e.g., inpatient, outpatient)
- "verificationGsPath": Storage path for consolidated verification documentation
- "fileReference": Resolved file reference for accessing verification documents

#### Field: "allResponsesReceived"

Boolean indicating whether any procedure timed out without receiving a response from the payer.

- "true": No procedures timed out - all submissions either succeeded or failed with actual payer responses
- "false": At least one procedure timed out waiting for a payer response (check for "exceptionComment" containing ""Portal did not respond within timeout"")

**Important:** Even when "false", you still receive authorization submissions for all procedures, including synthetic exception submissions for timed-out procedures. Use this field to determine if you need to retry or escalate.

#### Field: "errorResponse"

Reserved for error payloads. Currently not populated in the EviCore authorization path (errors result in block failures instead).

**Example Output**:

\`\`\`json
{
  "outputVersion": "authorization_submissions_v2",
  "allResponsesReceived": true,
  "authorizationStatus": {
    "status": "Approved",
    "lastUpdated": "2025-11-17T15:30:00Z"
  },
  "authorizationSubmissions": [
    {
      "procedureIds": ["proc_123"],
      "status": "Approved",
      "authorizationNumber": "AUTH2025001234",
      "certificationNumber": "CERT789456",
      "effectiveDate": "2025-11-20",
      "expirationDate": "2025-12-20",
      "approvedServices": [
        {
          "cptCode": "70553",
          "quantity": 1,
          "description": "MRI Brain with contrast"
        }
      ]
    },
    {
      "procedureIds": ["proc_124"],
      "status": "Denied",
      "authorizationNumber": "AUTH2025001235",
      "denialReason": "Not medically necessary",
      "exceptionComment": "Additional documentation required"
    }
  ]
}
\`\`\`

### Service Review Submissions Output

**Used when:** Submitting NOA or service reviews via Availity API

**Note:** PNT Data submissions are persisted on the patient order but do not currently populate this block output structure. For PNT Data, check the patient order directly.

**Output Structure:**

The block returns an object with the following fields:

- "outputVersion": Always set to ""service_review_submissions""
- "allResponsesReceived": Boolean indicating submission completion
- "serviceReviewSubmissions": Array of service review submission objects (optional)
- "errorResponse": Error details if submission failed (optional)

#### Field: "outputVersion"

Always ""service_review_submissions"" for this output type. Use this to identify which output structure you're working with.

#### Field: "serviceReviewSubmissions"

Array of enriched service review submission objects. Each submission in the array contains:

**Submission Information:**
- "submissionMethod": How the request was submitted - will be "API" for Availity submissions
- "payorPortal": Which payer portal handled the request (e.g., "AVAILITY", "PNT_DATA")

**Status Information:**
- "payorX12StatusCode": Standardized X12 status code:
  - "A1": Certified in total
  - "A2": Partial certification
  - "A3": Not certified
  - "A4": Pending additional information
  - "A6": Modified certification
  - "C": Canceled
  - "CT": Contact payer
  - "NA": No action required
  - "51": Complete
  - "71": Term expired
- "payorStatusMetadata": Structured status details from the payer (e.g., detailed status codes, status reasons, and other payer-specific information)
- "lifeCycleStatus": Internal processing status - one of: "NEW", "SUBMITTED", "ERROR"
- "lifeCycleStatusMetadata": Additional internal metadata (includes flow, run, and block information)

**Certification Details:**
- "certificationNumber": Payer's certification or tracking number (e.g., NOA ID)
- "certificationIssueDate": When the certification was issued
- "certificationEffectiveDate": When the certification becomes effective
- "certificationExpirationDate": When the certification expires (if applicable)

**Additional Identifiers:**
- "referenceNumber": Additional reference number from payer
- "controlNumber": Control number from payer
- "traceNumbers": Array of trace numbers for tracking

**Dates:**
- "requestDate": When the request was submitted

**Notes and Documentation:**
- "payorNotes": Free-form notes or comments from the payer
- "verificationGsPath": Storage path for verification file
- "fileReference": Resolved file reference for accessing verification documents
- "rawOutput": Original response body from the payer (e.g., Availity JSON), useful for debugging and traceability

#### Field: "allResponsesReceived"

Boolean indicating whether the submission flow has completed from the block's perspective.

- For Availity API submissions, this is always "true" as responses are synchronous (or treated as final when an error is returned)
- Use this field to determine if the submission workflow is complete

#### Field: "errorResponse"

Present only when the submission failed with a structured error response from the payer portal.

- Contains the raw error response (for example, Availity error payload with user message, developer message, and errors array)
- When "errorResponse" is set, "serviceReviewSubmissions" will be omitted
- "allResponsesReceived" will still be "true" (the block considers the flow complete, but failed)
- Use this to branch your flow to handle errors appropriately (e.g., retry logic, notifications, escalation)

**Example Output**:

\`\`\`json
{
  "outputVersion": "service_review_submissions",
  "allResponsesReceived": true,
  "serviceReviewSubmissions": [
    {
      "submissionMethod": "API",
      "payorPortal": "Availity",
      "payorX12StatusCode": "A1",
      "statusDescription": "Approved",
      "certificationNumber": "NOA20251117001",
      "effectiveDate": "2025-11-17",
      "notes": "Authorization approved for inpatient admission"
    }
  ]
}
\`\`\`

### Understanding Status Codes

Common X12 278 status codes you'll encounter:

| Code | Meaning | Description |
|------|---------|-------------|
| A1 | Approved | Service is approved as requested |
| A2 | Partial Approval | Some services approved, others denied or modified |
| A3 | Denied | Service request is denied |
| A4 | Pended | Request is pending, awaiting additional information |
| A6 | Modified | Service approved but with modifications |
| C | Contact Payer | Submitter should contact payer for more information |
| CT | Cancelled | Previous authorization has been cancelled |

---

## Common Use Cases

### Prior Authorization - Outpatient Procedure

**Scenario**: A patient needs an MRI with contrast. Your EHR has sent a 278 request message, and you need to submit it to the patient's insurance payer for prior authorization.

**Flow Configuration**:

1. **Start Block**: Triggered by inbound 278 message from EHR
2. **Patient Order Creation**: Parse 278 and create patient order
3. **Build Custom Payload**: Transform patient order into payer-specific format
4. **Payor Communication Engine**: Submit authorization request
5. **Process Response**: Write results back to EHR

**Block Configuration**:

\`\`\`json
{
  "submissionMode": "278",
  "payorPortal": "EviCore",
  "patientOrder": "{{CreatePatientOrder.data.patientOrderJson}}",
  "patientInsuranceId": "{{CreatePatientOrder.data.primaryInsuranceId}}",
  "customPayload": "ISA*00*          *00*          *ZZ*HOSPITALNAME   *ZZ*EVICORE        *251117*1539*^*00501*000143855*0*P*:~GS*HI*HOSPITALNAME*EVICORE*20251117*1539*143855*X*005010X217~ST*278*143855*005010X217~BHT*0007*13*PATIENT001*20251117*1539~HL*1**20*1~NM1*X3*2*Aetna*****PI*AETNA~HL*2*1*21*1~NM1*1P*1*PHYSICIAN*JOHN*A*DR*XX*1234567890~N3*100 MEDICAL CENTER BLVD~N4*BOSTON*MA*02101~PER*IC*TE*6175551234~HL*3*2*22*1~NM1*IL*1*DOE*JOHN*M***MI*MEM123456789~REF*EJ*GRP987654~N3*50 MAIN STREET~N4*CAMBRIDGE*MA*02139~DMG*D8*19800515*M~HL*4*3*EV*1~TRN*1*G12141267900031500~UM*HS*I*6*22:B~DTP*AAH*D8*20251125~HI*ABF:I10.00~HSD*VS*1~NM1*77*2*HOSPITAL NAME*****XX*0987654321~N3*100 MEDICAL CENTER BLVD~N4*BOSTON*MA*02101~PER*IC*TE*6175551234~HL*5*4*SS*0~TRN*1*A942689900031500~SV1*HC:70553**UN*1~SE*30*143855~GE*1*143855~IEA*1*000143855~",
  "timeout": { "value": 48, "units": "hours" },
  "isTest": false
}
\`\`\`

**Expected Output**:

\`\`\`json
{
  "outputVersion": "authorization_submissions_v2",
  "allResponsesReceived": true,
  "authorizationSubmissions": [
    {
      "procedureIds": ["proc_789"],
      "status": "Approved",
      "authorizationNumber": "AUTH2025123456",
      "certificationNumber": "CERT789456123",
      "effectiveDate": "2025-11-25",
      "expirationDate": "2025-12-25",
      "approvedServices": [
        {
          "cptCode": "70553",
          "quantity": 1,
          "description": "MRI Brain with and without contrast"
        }
      ]
    }
  ]
}
\`\`\`

### Notice of Admission (NOA)

**Scenario**: A patient has been admitted to your hospital for an inpatient stay. You need to notify the insurance payer within 24 hours per their requirements.

**Flow Configuration**:

1. **Start Block**: Triggered by ADT message from EHR (patient admission)
2. **Get Patient Order**: Retrieve or create patient order for the admission
3. **Payor Communication Engine**: Submit NOA to Availity
4. **Log Results**: Record NOA submission confirmation

**Block Configuration**:

\`\`\`json
{
  "submissionMode": "API",
  "payorPortal": "Availity",
  "patientOrder": "{{GetPatientOrder.data.patientOrderJson}}",
  "patientInsuranceId": "{{GetPatientOrder.data.primaryInsuranceId}}",
  "timeout": { "value": 10, "units": "minutes" },
  "isTest": false
}
\`\`\`

**Expected Output**:

\`\`\`json
{
  "outputVersion": "service_review_submissions",
  "allResponsesReceived": true,
  "serviceReviewSubmissions": [
    {
      "submissionMethod": "API",
      "payorPortal": "Availity",
      "payorX12StatusCode": "A1",
      "statusDescription": "Notice Received",
      "certificationNumber": "NOA20251117001",
      "effectiveDate": "2025-11-17",
      "notes": "Notice of admission received and logged"
    }
  ]
}
\`\`\`

---

## Integration & Testing

### Prerequisites

Before using the Payor Communication Engine block, ensure you have:

#### Required Patient Order Data

Your patient order must include:

- **Patient Demographics**
  - Full name (first, last, middle initial)
  - Date of birth
  - Gender
  - Address

- **Insurance Information**
  - Payer name and ID
  - Member/subscriber ID
  - Group number (if applicable)
  - Relationship to subscriber

- **Procedure Details** (for authorizations)
  - CPT codes for procedures
  - Diagnosis codes (ICD-10)
  - Procedure descriptions
  - Quantity/units of service
  - Service dates (requested or scheduled)

- **Provider Information**
  - Rendering provider NPI and name
  - Servicing facility NPI and name
  - Provider addresses
  - Contact information

- **Encounter Information**
  - Place of service code
  - Admission date (for inpatient)
  - Discharge date (if known)

### EHR Integration Considerations

#### Message Formats

The block can work with data from various EHR message formats:

- **X12 278 Inbound**: Parse incoming 278 requests from your EHR and transform into patient orders
- **SFTP Patient Order Feeds**: Ingested the required patient and patient order information then trigger the flow off of a scenario

#### Write-Back Requirements

After receiving responses from payers, you'll typically need to write results back to your EHR:

- **Format Selection**: Choose the appropriate format for your EHR system
  - Epic: Typically expects X12 278 responses
  - Cerner: May expect HL7 messages

- **Data Mapping**: Map Notable's normalized response data to your EHR's expected format

- **Routing**: Ensure your integration engine is configured to route responses to the correct EHR system and environment

---

## Troubleshooting

### Common Issues

#### Missing Procedure Information

**Symptom**: Block fails with error "Procedures are required in patient order for submission"

**Cause**: Patient order doesn't include procedure details with CPT codes

**Resolution**:
- Verify your patient order includes a "procedures" array
- Ensure each procedure has a "cptCode" field populated
- For authorization requests, at least one procedure with a CPT code is required
- Check that your upstream blocks (patient order creation/retrieval) are correctly populating procedure data

#### Response Timeouts

**Symptom**: "allResponsesReceived" is "false" or submissions have exception comments indicating "Portal did not respond within timeout"

**Cause**: Payer took longer to respond than the configured timeout period

**Resolution**:
1. Increase timeout duration for EDI submissions:

2. Check if timeout is appropriate for the submission method:
   - API submissions: 5-15 minutes is typically sufficient
   - EDI submissions: Hours may be needed

3. Verify payer system is operational (not experiencing outages)

4. For EviCore submissions with partial responses:
   - Some procedures may respond quickly while others take longer
   - Check individual procedure submission statuses
   - Consider whether partial results are acceptable for your workflow

#### Write-Back Not Appearing in EHR

**Symptom**: Authorization approved in Notable but not showing in EHR system

**Cause**: Write-back message format incorrect or routing configuration issue

**Resolution**:
1. Verify your write-back flow is correctly configured
2. Check that the output format matches EHR expectations
3. Confirm routing/interface engine is properly forwarding messages
4. Review integration engine logs for transmission errors
5. Validate that EHR system is configured to accept messages from Notable

#### Custom Payload Validation Errors

**Symptom**: Block fails with "Invalid custom payload" error

**Cause**: Custom payload doesn't match the expected format for the payer portal

**Resolution**:

For **API submissions (Availity)**:
- Ensure payload is valid JSON
- Verify all required fields per Availity's API specification
- Check data types match expected values
- Validate date formats (typically ISO 8601)

For **EDI submissions (EviCore, PNT Data)**:
- Verify payload is a valid X12 278 transaction
- Check segment ordering and required segments
- Ensure delimiters are consistent throughout
- Validate element separators and segment terminators
- Confirm ISA and GS headers match payer requirements

#### Duplicate Submission Errors

**Symptom**: Error indicating submission already exists for this patient order

**Cause**: Attempting to submit the same authorization request multiple times

**Resolution**:
- Check if patient order already has an authorization submission
- Review flow logic to prevent duplicate triggers
- If this is an updated/revised request, create a new patient order
- Implement idempotency checks in your flow

### Understanding Error Messages

Common error messages and their meanings:

| Error Message | Meaning | Action |
|--------------|---------|--------|
| "Invalid submission mode" | Submission mode not recognized | Use "API" or "278" |
| "Invalid payor portal for submission mode" | Incompatible mode/portal combination | Check payer-specific sections for valid combinations |
| "Procedures are required in patient order" | No procedures with CPT codes found | Add procedure details to patient order |
| "customPayload is required" | Missing required custom payload | Provide custom payload for EDI submissions |
| "Portal did not respond within timeout" | No response received in timeout period | Increase timeout or check payer system status |

### Payer-Specific Considerations

#### Availity

- **Payer Outages**: Availity API returns specific error codes during payer system outages. The block automatically retries these.
- **Mock Scenarios**: In test mode, you can use mock scenario headers to simulate different response types
- **Rate Limiting**: Availity has rate limits on API calls. Space out high-volume submissions if needed.

#### EviCore

- **Incremental Responses**: Responses arrive per procedure, not all at once
- **Weekend/Holiday Delays**: Authorization responses may be delayed during non-business hours
- **Specialty Variations**: Different specialties (radiology, cardiology) may have different response times

#### PNT Data

- **No Test Mode**: Only production submissions are supported
- **File Processing Delays**: Response times can vary based on file processing schedules
- **Multi-Payer Support**: PNT Data acts as a clearinghouse for multiple payers

---

## Reference

### Configuration Field Reference

| Field | Type | Required | Default | Description |
|-------|------|----------|---------|-------------|
| "submissionMode" | string | Yes | - | Submission method: "API" or "278" |
| "payorPortal" | string | Yes | - | Payer portal: "Availity", "EviCore" or "PNT Data" |
| "patientOrder" | string | Yes | - | JSON string of patient order data |
| "patientInsuranceId" | string | Yes | - | ID of patient insurance data|
| "customPayload" | string | Conditional | - | Custom request payload (required for EDI) |
| "timeout" | duration | Yes | 1 hour | Maximum wait time for responses |
| "isTest" | boolean | No | true | Use test/sandbox mode |

### Status Code Reference

#### X12 278 Status Codes

| Code | Status | Description | Next Steps |
|------|--------|-------------|------------|
| **A1** | Approved | Service approved as requested | Proceed with service |
| **A2** | Partial Approval | Some services approved | Review approved vs. denied services |
| **A3** | Denied | Service request denied | Review denial reason, consider appeal |
| **A4** | Pended | Pending more information | Submit additional documentation |
| **A6** | Modified | Approved with modifications | Review modifications, confirm with provider |
| **C** | Contact Payer | Contact payer for details | Call payer customer service |
| **CT** | Cancelled | Previously issued authorization cancelled | Re-submit if service still needed |
| **NA** | Not Applicable | Status not applicable | Review request details |
| **51** | Deferred | Decision deferred | Wait for follow-up from payer |
| **71** | Denied - Medical Necessity | Denied due to lack of medical necessity | Provide clinical justification |

#### Authorization Submission Status Values

| Status | Meaning | Description |
|--------|---------|-------------|
| "Approved" | Authorization granted | Proceed with scheduled service |
| "Denied" | Authorization denied | Service not authorized, review reason |
| "Pending" | Awaiting decision | Payer still reviewing request |
| "Exception" | Processing error | Issue with submission or portal |

### Glossary

**Authorization Request**
A request to an insurance payer for approval to perform a medical procedure or service before it is rendered.

**CPT Code (Current Procedural Terminology)**
Standardized codes used to describe medical, surgical, and diagnostic procedures. Required for most authorization requests.

**EDI (Electronic Data Interchange)**
The automated exchange of business documents in a standard electronic format between organizations. In healthcare, includes formats like X12 278.

**HL7 (Health Level 7)**
A set of international standards for the transfer of clinical and administrative data between healthcare information systems.

**ICD-10 (International Classification of Diseases)**
Diagnostic codes used to describe patient conditions and diagnoses. Often required alongside CPT codes for authorizations.

**NPI (National Provider Identifier)**
A unique 10-digit identification number assigned to healthcare providers in the United States. Required for payer submissions.

**NOA (Notice of Admission)**
A notification sent to an insurance payer when a patient is admitted to a hospital for an inpatient stay. Distinct from prior authorization - it's informational rather than a request for approval.

**Patient Order**
Notable's internal data structure representing a medical order (procedure, referral, admission) with all associated patient, insurance, provider, and procedure information.

**Payer Portal**
An online system provided by insurance payers (or their intermediaries) for submitting and managing authorization requests.

**Prior Authorization**
A requirement by insurance payers that healthcare providers obtain approval before performing certain medical services to ensure coverage.

**Service Review**
The broader healthcare industry term encompassing prior authorizations, notices of admission, and related payer communications about medical services.

**X12 278**
A specific EDI transaction set used for healthcare service review (inquiry, authorization, notice of admission) requests and responses.

`;
